import * as React from "react";
import { ChevronDown, LayoutGrid, Palette } from "lucide-react";
import { Button, DropdownMenu } from "@/components/ui";
import { ContentPageTheme } from "@/services/bedrock/dtos/ContentPageTheme";
import { baseThemeOption } from "../baseTheme";
import { useEditorThemes } from "../hooks/useEditorThemes";

interface ThemeSwitcherProps {
  value: number | null;
  /** The package's coach; their default theme stands in for the base preview. */
  coachId: number | null;
  onChange: (themeId: number | null) => void;
  /** Opens every theme side by side; adds the menu's last item. */
  onCompareAll?: () => void;
  disabled?: boolean;
}

interface ThemeItem {
  key: string;
  themeId: number | null;
  label: string;
  theme: ContentPageTheme | null;
}

export const STANDARD_BRANDING_LABEL = "Standard branding";

export const ThemeSwatches: React.FC<{ theme: ContentPageTheme }> = ({ theme }) => (
  <span className="inline-flex overflow-hidden rounded-full border border-slate-200" aria-hidden="true">
    {[theme.backgroundColor, theme.primaryCtaColor, theme.textColor].map((color, i) => (
      <span key={i} className="size-3" style={{ backgroundColor: color }} />
    ))}
  </span>
);

/**
 * A compact theme menu for the preview: the current theme's swatches and name on the trigger, every
 * theme in the menu, and a way into comparing them all. The first item is the base preview (no
 * theme): it's the coach's default theme when they have one, so that theme isn't listed twice.
 */
export const ThemeSwitcher: React.FC<ThemeSwitcherProps> = ({ value, coachId, onChange, onCompareAll, disabled }) => {
  const { options } = useEditorThemes();

  const items = React.useMemo<ThemeItem[]>(() => {
    const base = baseThemeOption(options, coachId);
    return [
      base
        ? { key: "base", themeId: null, label: base.label, theme: base.theme }
        : { key: "base", themeId: null, label: STANDARD_BRANDING_LABEL, theme: null },
      ...options
        .filter(option => option !== base)
        .map(option => ({
          key: String(option.theme.id),
          themeId: option.theme.id,
          label: option.label,
          theme: option.theme,
        })),
    ];
  }, [options, coachId]);
  // The default theme's own id (from a link) lands on the base item, which is that theme.
  const selected = items.find(item => item.themeId === value || item.theme?.id === value) ?? items[0];
  const name = selected.theme?.name ?? STANDARD_BRANDING_LABEL;

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger
        disabled={disabled}
        render={props => (
          <Button {...props} variant="ghost" size="sm" aria-label={`Theme: ${name}`} className="max-w-48 gap-1.5 px-2">
            {selected.theme ? (
              <ThemeSwatches theme={selected.theme} />
            ) : (
              <Palette className="size-3.5 text-slate-500" aria-hidden="true" />
            )}
            <span className="truncate">{name}</span>
            <ChevronDown className="size-3.5 text-slate-400" aria-hidden="true" />
          </Button>
        )}
      />
      <DropdownMenu.Content align="end" className="max-h-80 min-w-60">
        <DropdownMenu.Group>
          <DropdownMenu.Label>Preview with theme</DropdownMenu.Label>
          <DropdownMenu.RadioGroup
            value={selected.key}
            onValueChange={key => onChange(items.find(item => item.key === key)?.themeId ?? null)}
          >
            {items.map(item => (
              <DropdownMenu.RadioItem key={item.key} value={item.key} closeOnClick>
                {item.theme && <ThemeSwatches theme={item.theme} />}
                {item.label}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Group>
        {onCompareAll && (
          <>
            <DropdownMenu.Separator />
            <DropdownMenu.Item onClick={onCompareAll}>
              <LayoutGrid aria-hidden="true" />
              Compare all themes…
            </DropdownMenu.Item>
          </>
        )}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};
